import { cleanStacked, isStacked, layoutCells, parseLayout } from "@/lib/layouts";
import { createContext, useContext } from "react";

export const LinesContext = createContext({ choices: [], rates: {} });
export const useLines = () => useContext(LinesContext);
export const UNIT_LABEL = { piece: "per piece", sash: "per opening sash", panel: "per panel", sqft: "per sq ft", lin_ft: "per linear ft" };
export const OVERSIZE = "Oversized unit: check with supplier";

export const cleanLayout = (v, def = "X") => {
  if (isStacked(v)) return cleanStacked(v) || cleanLayout("", def);
  if (!v && isStacked(def)) return cleanStacked(def) || "X";
  const s = String(v || "").toUpperCase().replace(/0/g, "O").replace(/[^OX]/g, "").slice(0, 8);
  return (s || String(def || "X").replace(/\|/g, "") || "X").split("").join("|");
};

export const CATEGORY_LABELS = {
  casement: "Casement", awning: "Awning (Top-Hung)", crank: "Crank Casement", fixed: "Fixed/Picture", single_hung: "Single Hung",
  double_hung: "Double Hung", sliding_window: "Sliding Window", sliding_door: "Sliding Patio Door", pocket_door: "Pocket Door",
  swing_door: "Swing/French Door", screen: "Screen", bifold_door: "Bi-fold Door", bifold_window: "Bi-fold Window",
};
const foldSum = (f) => String(f || "").replace(/\D/g, "").split("").reduce((s, d) => s + Number(d), 0);
export const foldPanels = (it) => Math.max(2, Math.min(8, Number(it.panels) || foldSum(it.fold) || 3));
export const foldConfig = (it) => {
  const n = foldPanels(it), f = String(it.fold || "");
  return f && foldSum(f) === n ? f : `${n}L`;
};
export const NO_SMART = "No smart lock price set";

export const SHAPE_STYLES = [
  ["half_round", "Half Round"], ["segment", "Segment (eyebrow)"], ["gothic", "Gothic"], ["quarter_l", "Quarter Round (left)"],
  ["quarter_r", "Quarter Round (right)"], ["trapezoid_l", "Trapezoid (high left)"], ["trapezoid_r", "Trapezoid (high right)"],
  ["pentagon", "Pentagon (peak)"], ["triangle", "Triangle"], ["ellipse", "Ellipse / Oval"], ["round", "Round (circle)"],
];
export const styleLabel = (v) => (SHAPE_STYLES.find(([k]) => k === v) || [0, v])[1];

// Same math as backend/components.py component_cost (Pricing Calculation Guide): m² = W×H ÷ 1550.0031, every step rounded to cents.
const SQIN = 1550.0031, SQ = 10.7639;
const c2 = (x) => Math.round(Number(Number(x).toPrecision(12)) * 100 + 1e-7) / 100;
const m2r = (r) => Math.round((r || 0) * SQ * 100) / 100;
const ft = (m2) => `${((m2 * SQIN) / 144).toFixed(2)} sq ft (${m2.toFixed(3)} m²)`;
const billed = (act, b) => ft(b) + (b > act + 1e-9 ? ` (actual ${ft(act)}, 1.00 m² min)` : "");
export const GLASS_OVER = "Special quote: glass over 8 m²";
const sashLimits = (line) => {
  const d = line.category === "awning" ? [59.02, 43.31] : [33.43, 59.06];
  return [line.sash_max_w || d[0], line.sash_max_h || d[1]];
};
const pickTrack = (tracks, name) => {
  const plain = (v) => String(v || "").replace("Tile ", "");
  return tracks.find((x) => x.name === name) || tracks.find((x) => plain(x.name) === plain(name)) || tracks[0] || { name: "Track", rate: 0 };
};

function panes(line, it, w, h) {
  if (line.kind === "bifold") {
    const n = foldPanels(it), pw = (it.panel_widths || []).map(Number);
    return (pw.length === n && pw.every(Boolean) ? pw : Array(n).fill(w / n)).map((x) => ["X", x, h]);
  }
  if (line.kind === "slider") {
    const p = String(it.config || "XO");
    return p.split("").map((k) => [k, w / p.length, h]);
  }
  const lay = String(it.layout || line.default_layout || "X");
  let out;
  if (isStacked(lay) && cleanStacked(lay)) {
    out = layoutCells(parseLayout(lay), 0, 0, w, h, (it.panel_widths || []).map(Number), (it.row_heights || []).map(Number)).map((c) => [c[0], c[3], c[4]]);
  } else {
    const p = lay.replace(/\|/g, "");
    const pw = (it.panel_widths || []).map(Number);
    const widths = pw.length === p.length && pw.every(Boolean) ? pw : p.split("").map(() => w / p.length);
    out = p.split("").map((k, i) => [k, widths[i], h]);
  }
  const typed = typedSashes(it);
  if (typed.length > out.filter((p) => p[0] === "X").length && out.every((p) => p[0] === "X")) return typed.map(([tw, th]) => ["X", tw, th]);
  if (out.length === 1) return out;
  let n = 0;
  const mapped = out.map((p) => (p[0] === "X" && n < typed.length ? ["X", ...typed[n++]] : p));
  const fixed = mapped.filter((p) => p[0] !== "X").length, left = w - mapped.filter((p) => p[0] === "X").reduce((s, p) => s + p[1], 0);
  if (!typed.length || !fixed || /[/(]/.test(lay) || left <= 0) return mapped;
  return mapped.map((p) => (p[0] === "X" ? p : [p[0], left / fixed, p[2]]));
}

export const SASH_FIT = "Typed sash sizes are bigger than the unit: check the sizes";
const sashFit = (it, ps, w, h) => {
  const typed = typedSashes(it);
  if (!typed.length) return true;
  const flat = !/[/(]/.test(it.layout || "");
  return !(Math.max(...typed.map((s) => s[0])) > w + 0.6 || Math.max(...typed.map((s) => s[1])) > h + 0.6 || (flat && ps.reduce((s, p) => s + p[1], 0) > w + 0.6));
};

const typedSashes = (it) => (it.sash_sizes || []).filter((s) => s && s.length === 2 && Number(s[0]) && Number(s[1])).map((s) => s.map(Number));
const g = (x) => +Number(x).toFixed(2);
const sashCheck = (pw, ph, lw, lh) => {
  const over = [...(pw > lw ? [`width ${g(pw)} > ${g(lw)}`] : []), ...(ph > lh ? [`height ${g(ph)} > ${g(lh)}`] : [])];
  return over.length ? `oversized: ${over.join(" and ")} in` : `standard, limit ${g(lw)}×${g(lh)} in`;
};

// Mirrors backend components.infer_layout: an explicit layout wins; "double / pair / french / XX" splits into sections.
const MULTI_SASH = ["casement", "crank", "awning", "tilt_turn", "swing_door"];
const GLASS_WORDS = /\b(?:low[- ]?e|insulated|argon|tempered|laminated)[- ]+(?:double|dbl)\b|\b(?:double|dbl)[- ]?(?:glaz\w*|pane\w*|glass|insul\w*|low[- ]?e|argon|ig\w*|seal\w*|hung|lock\w*|hook\w*|tempered|strength)\b/gi;
const PAIR_RX = /\b(pair|french|twin|double|dbl|2[- ]?(?:sash|leaf|leaves|panel|lite)s?|two[- ](?:sash|leaf|leaves|panel)s?)\b/i;
const TRIPLE_RX = /\b(triple(?![- ]?(?:glaz|pane|glass|layer))|three[- ](?:sash|leaf|panel)s?|3[- ]?(?:sash|leaf|panel)s?)\b/i;
export function inferLayout(category, def, text = "", current = "") {
  if (current) return cleanLayout(current, def);
  if (category === "double_hung") return "X/X";
  const t = String(text).replace(GLASS_WORDS, " ");
  if (MULTI_SASH.includes(category)) {
    const code = t.toUpperCase().match(/(?<![A-Z])([XO](?:[|-]?[XO]){1,7})(?![A-Z])/);
    if (code) return code[1].replace(/[|-]/g, "").split("").join("|");
    const n = TRIPLE_RX.test(t) ? 3 : PAIR_RX.test(t) ? 2 : 1;
    if (n > 1) return Array(n).fill("X").join("|");
  }
  return cleanLayout("", def);
}

export function componentCost(line, it) {
  const w = Number(it.width_in) || 0, h = Number(it.height_in) || 0;
  const bif = line.kind === "bifold";
  const a = bif ? +((w * h) / SQIN).toFixed(6) : (w * h) / SQIN, mn = (line.min_area || 0) / SQ, bill = Math.max(a, mn);
  const parts = [], warns = [];
  if (line.kind === "slider") {
    const t = pickTrack(line.tracks || [], it.track), r = m2r(t.rate);
    parts.push([`Total area (${t.name}) ${billed(a, bill)} × $${r.toFixed(2)}/m²`, c2(bill * r)]);
  } else {
    const r = m2r(line.frame_rate);
    parts.push([`Total area ${billed(a, bill)} × $${r.toFixed(2)}/m²`, c2(bill * r)]);
  }
  const ps = panes(line, it, w, h);
  if (!bif && line.kind !== "slider" && !sashFit(it, ps, w, h)) warns.push(SASH_FIT);
  let n = 0;
  if (!bif && line.kind !== "slider" && (line.sash_std_rate || line.sash_over_rate)) {
    ps.forEach(([k, pw0, ph0], i) => {
      if (k !== "X") return;
      const typed = typedSashes(it);
      const [pw, ph] = ps.length === 1 && typed.length ? typed[0] : [pw0, ph0];
      n += 1;
      const [lw, lh] = sashLimits(line), over = pw > lw || ph > lh, r = m2r(over ? line.sash_over_rate : line.sash_std_rate);
      const smin = line.sash_min_area == null ? line.min_area || 0 : line.sash_min_area || 0;
      const sa = (pw * ph) / SQIN, sb = Math.max(sa, smin / SQ);
      parts.push([`Sash ${i + 1} ${g(pw)}×${g(ph)} in (${sashCheck(pw, ph, lw, lh)}) ${billed(sa, sb)} × $${r.toFixed(2)}/m²`, c2(sb * r)]);
    });
  }
  if (bif) parts.push([`Bi-fold ${foldConfig(it)}: ${ps.length} panels ${ps.map(([, pw, ph]) => `${(pw * 25.4).toFixed(0)}×${(ph * 25.4).toFixed(0)} mm`).join(", ")} (glass priced per panel, 3 hinges per panel included)`, 0]);
  else if (ps.length > 1 && line.kind !== "slider") parts.push([`Sections (${ps.length} panes, glass priced per pane): ${ps.map(([k, pw, ph]) => `${k} ${g(pw)}×${g(ph)} in`).join(", ")}`, 0]);
  const tiers = [...(line.glass_tiers || [])].sort((x, y) => x.from_sqft - y.from_sqft);
  const up = (line.glass_upgrades || []).find((u) => u.name === it.glass_upgrade);
  ps.forEach(([, pw, ph], i) => {
    const pa = bif ? +((pw * ph) / SQIN).toFixed(6) : (pw * ph) / SQIN;
    let tier = tiers.find((t) => +(t.from_sqft / SQ).toFixed(2) < pa && pa <= +(t.to_sqft / SQ).toFixed(2) + 1e-9);
    if (pa > 8 + 1e-9) { warns.push(GLASS_OVER); tier = tier || tiers[tiers.length - 1]; }
    const r = up ? m2r(up.rate) : tier ? m2r(tier.rate) : 0;
    if (!r && bif) parts.push([`Glass panel ${i + 1} ${pa.toFixed(3)} m²: no surcharge band`, 0]);
    if (r) parts.push([`${up ? `Glass upgrade ${up.name}` : `Glass surcharge ${(tier.from_sqft / SQ).toFixed(0)}–${(tier.to_sqft / SQ).toFixed(0)} m² band`}, pane ${i + 1} = ${ft(pa)} × $${r.toFixed(2)}/m²`, c2(pa * r)]);
  });
  if (line.crate_rate) { const r = m2r(line.crate_rate); parts.push([`Crate ${billed(a, bill)} × $${r.toFixed(2)}/m²`, c2(bill * r)]); }
  if (bif) {
    const lim = line.hinge_limit_mm || 2400, hr = line.hinge_rate || 0;
    if (h * 25.4 > lim + 0.5) parts.push([`Extra hinges: ${ps.length} panels × 1 × $${hr.toFixed(2)} (height ${(h * 25.4).toFixed(0)} mm > ${lim} mm)`, c2(ps.length * hr)]);
    if (it.smart_lock) {
      const sr = line.smart_lock_rate;
      parts.push([sr == null ? `Smart lock: ${NO_SMART}` : "Smart lock (replaces the regular lock)", c2(sr || 0)]);
      if (sr == null) warns.push(NO_SMART);
    } else parts.push(["Regular lock (once per unit)", c2(line.lock_rate || 0)]);
  }
  const count = { piece: 1, sash: ps.filter((p) => p[0] === "X").length, panel: ps.length, lin_ft: w / 12 };
  (it.extras || []).forEach((sel) => {
    const e = (line.extras || []).find((x) => x.name === sel.name), q = Number(sel.qty);
    if (!e || !q) return;
    if (e.unit === "sqft") parts.push([`${e.name} ${ft(bill)} × $${m2r(e.rate).toFixed(2)}/m² × ${q}`, c2(bill * m2r(e.rate) * q)]);
    else parts.push([`${e.name} (${q} × ${e.unit})`, c2(e.rate * count[e.unit] * q)]);
  });
  const shape = it.component?.shape;
  if (shape) parts.push([`Shape: ${shape.name}`, c2(shape.unit === "piece" ? shape.price : bill * m2r(shape.price))]);
  const sum = () => parts.reduce((s, [, v]) => s + v, 0);
  const uplift = (pct) => { const b = c2(sum()); return c2(b * (1 + pct / 100)) - b; };
  if (line.surcharge_pct) parts.push([`Tilt-Turn uplift +${line.surcharge_pct}%`, uplift(line.surcharge_pct)]);
  if (it.series === "element" && it.element_pct) parts.push([`Element series +${it.element_pct}%`, uplift(it.element_pct)]);
  if (line.base_price) parts.push(["Base price", c2(line.base_price)]);
  const sub = c2(sum());
  let unit = Math.max(sub, line.min_price || 0);
  if (line.min_price && unit > sub) parts.push(["Minimum price applied", c2(unit - sub)]);
  const sc = it.component?.screen;
  if (sc) {
    const dbl = it.screen_pull === "double" && sc.min_area_double;
    const smin = (dbl ? sc.min_area_double : sc.min_area || 0) / SQ, sb = Math.max(a, smin), r = m2r(sc.rate), amt = c2(sb * r);
    parts.push([`Screen: ${sc.name} ${billed(a, sb).replace("1.00 m² min", `${smin.toFixed(2)} m² min`)} × $${r.toFixed(2)}/m²`, amt]);
    unit = c2(unit + amt);
    const sf = (w * h) / 144;
    if ((sc.valid_min && sf < sc.valid_min) || (sc.valid_max && sf > sc.valid_max)) warns.push(SCREEN_RANGE);
  }
  if (w > 94.49 && h > 94.49) warns.unshift(OVERSIZE);
  return { unit, parts, warn: [...new Set(warns)].join(" · ") };
}

export const SCREEN_RANGE = "Screen size outside supplier range: check price";
export const PLEATED = ["casement", "awning", "crank", "single_hung", "double_hung", "sliding_window", "swing_door"];
export const screenSnap = (s) => s && { id: s.id, name: s.name, rate: s.frame_rate, min_area: s.min_area, min_area_double: s.min_area_double || 0,
  valid_min: s.valid_min || 0, valid_max: s.valid_max || 0 };
export const screensFor = (choices, line) => (line && !line.includes_screen ? choices.filter((c) => c.category === "screen" && (c.applies_to || []).includes(line.category)) : []);
